import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { type Weekday } from '../enums/enums.js';
import { CourseFilter } from '../filter/CourseFilter.js';

/**
 * @entity Course
 */
@Entity()
export class Course extends MongoEntity {
  public static override _class = 'Course';
  public static override _collectionName = 'courses';

  // todo: currently duplicated from courseHelper, remove redundancy
  private readonly _COURSE_TITLES = {
    CourseLernfoerderung: 'Lernförderung',
    CourseIntensive: 'Intensivkurs',
  };

  @Field(type => String, { default: v => v ?? '' })
  public name!: string;

  @Field(type => String, { default: v => v ?? GLOBAL_CONTEXT })
  public kpfID!: string;

  @Field(type => KPF, {
    resolve: function (this: Course) {
      return async () =>
        this.kpfID !== '' && this.kpfID !== GLOBAL_CONTEXT
          ? (loadMongoData(KPF, this.kpfID) as Promise<Maybe<KPF>>)
          : null;
    },
  })
  public kpf!: KPF;

  // Defaults to empty string because we map courses according to this field and maps cannot work with undefined
  @Field(type => String, { default: v => v ?? '' })
  public location!: string;

  @Field(type => String)
  public weekday: Maybe<Weekday>;

  @Field(type => DateTime)
  public startTime: Maybe<DateTime>;

  @Field(type => DateTime)
  public endTime: Maybe<DateTime>;

  @Field(type => DateTime)
  public plannedStartDate: Maybe<DateTime>;

  @Field(type => DateTime)
  public startDate: Maybe<DateTime>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public schuKoIDs: Maybe<string[]>;

  @DataLoadedField(type => [ChancenwerkUser], ({ schuKoIDs }) => schuKoIDs ?? [])
  public schuKos: Maybe<ChancenwerkUser[]>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public leKoIDs: Maybe<string[]>;

  @DataLoadedField(type => [ChancenwerkUser], ({ leKoIDs }) => leKoIDs ?? [])
  public leKos: Maybe<ChancenwerkUser[]>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public osStudentIDs!: string[];

  @DataLoadedField(type => [Student], ({ osStudentIDs }) => osStudentIDs)
  public osStudents!: Student[];

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public usStudentIDs!: string[];

  @DataLoadedField(type => [Student], ({ usStudentIDs }) => usStudentIDs)
  public usStudents!: Student[];

  @Field(type => DateTime)
  public archivedAfter: Maybe<DateTime>;

  public get stringRepresentation(): string {
    return `${this._COURSE_TITLES[this._class] as string} ${this.weekday ?? ''} ${
      this.location
    } ${this.startTime?.toFormat('HH:mm')}`;
  }

  /**
   * This function provides help and deduplication for assigning users to courses.
   * @param userToAssign The user which should be assigned
   * @param courseToAssignTo The new course for the user
   * @param options options object, with the array where the user should be assigned to ('leKo' | 'OS' | 'US') default = US, and the old course where the user should be unassigned
   */
  public static assignUserToCourse(
    userToAssign: ChancenwerkUser,
    courseToAssignTo: Course | null,
    options: { arrayToAssign: 'SchuKo' | 'LeKo' | 'OS' | 'US'; oldCourses?: Course[] } = {
      arrayToAssign: 'US',
    },
  ) {
    if (!userToAssign) throw new Error('Cannot assign falsy user!');

    // Unassign from old course if there is an array with that user
    if (options.oldCourses?.length) {
      for (const oldCourse of options.oldCourses) {
        let oldCourseInfo = {
          indexIDs: oldCourse.leKoIDs?.findIndex(uID => uID === userToAssign._id),
          indexUsers: oldCourse.leKos?.findIndex(u => u._id === userToAssign._id),
          array: 'LeKo',
        };
        if (oldCourseInfo.indexIDs === -1)
          oldCourseInfo = {
            indexIDs: oldCourse.schuKoIDs?.findIndex(uID => uID === userToAssign._id),
            indexUsers: oldCourse.schuKos?.findIndex(u => u._id === userToAssign._id),
            array: 'SchuKo',
          };
        if (oldCourseInfo.indexIDs === -1)
          oldCourseInfo = {
            indexIDs: oldCourse.osStudentIDs.findIndex(uID => uID === userToAssign._id),
            indexUsers: oldCourse.osStudents.findIndex(u => u._id === userToAssign._id),
            array: 'OS',
          };
        if (oldCourseInfo.indexIDs === -1)
          oldCourseInfo = {
            indexIDs: oldCourse.usStudentIDs.findIndex(uID => uID === userToAssign._id),
            indexUsers: oldCourse.usStudents.findIndex(u => u._id === userToAssign._id),
            array: 'US',
          };

        if (oldCourseInfo.indexIDs !== -1) {
          switch (oldCourseInfo.array) {
            case 'SchuKo':
              oldCourse.schuKoIDs?.splice(oldCourseInfo.indexIDs, 1);
              oldCourse.schuKos?.splice(oldCourseInfo.indexUsers, 1);
              break;
            case 'LeKo':
              oldCourse.leKoIDs?.splice(oldCourseInfo.indexIDs, 1);
              oldCourse.leKos?.splice(oldCourseInfo.indexUsers, 1);
              break;
            case 'OS':
              oldCourse.osStudentIDs.splice(oldCourseInfo.indexIDs, 1);
              oldCourse.osStudents.splice(oldCourseInfo.indexUsers, 1);
              break;
            case 'US':
              oldCourse.usStudentIDs.splice(oldCourseInfo.indexIDs, 1);
              oldCourse.usStudents.splice(oldCourseInfo.indexUsers, 1);
              break;
            default:
              break;
          }
        }
      }
    }

    // Assign to new Array
    if (courseToAssignTo) {
      let indexSchuKo: number, indexLeKo: number, indexOS: number, indexUS: number;

      switch (options.arrayToAssign) {
        case 'SchuKo':
          indexSchuKo = courseToAssignTo.schuKoIDs?.findIndex(uID => uID === userToAssign._id);
          if (indexSchuKo !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.schuKoIDs?.push(userToAssign._id);
          courseToAssignTo.schuKos.push(userToAssign);
          break;
        case 'LeKo':
          indexLeKo = courseToAssignTo.leKoIDs?.findIndex(uID => uID === userToAssign._id);
          if (indexLeKo !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.leKoIDs?.push(userToAssign._id);
          courseToAssignTo.leKos.push(userToAssign);
          break;
        case 'OS':
          indexOS = courseToAssignTo.osStudentIDs.findIndex(uID => uID === userToAssign._id);
          if (indexOS !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.osStudentIDs.push(userToAssign._id);
          courseToAssignTo.osStudents.push(userToAssign as Student);
          break;
        case 'US':
          indexUS = courseToAssignTo.usStudentIDs.findIndex(uID => uID === userToAssign._id);
          if (indexUS !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.usStudentIDs.push(userToAssign._id);
          courseToAssignTo.usStudents.push(userToAssign as Student);
          break;
        default:
          break;
      }
    }
  }

  @Subscription(type => [Course])
  public static subscribeCoursesByKPFID(
    @Arg('kpfID', type => [String]) kpfIDs: string[],
    @Arg('courseType', type => String) courseType: string = 'CourseLernfoerderung',
  ) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = { $or: [{ kpfID: { $in: kpfIDs } }, { openToAll: true }], _class: courseType };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(type => [Course])
  public static subscribeAllCoursesByKPFID(@Arg('kpfID', type => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = { $or: [{ kpfID }, { openToAll: true }] };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(type => [Course])
  public static subscribeCoursesByFilter(@Arg('filter', type => CourseFilter) filter: CourseFilter) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(type => [Course])
  public static subscribeCoursesByFilterForUser(
    @Arg('userId', type => ID) userId: string,
    @Arg('filter', type => CourseFilter) filter: CourseFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = {
        $and: [filter.getMongoFilter(), { $or: [{ osStudentIDs: userId }, { usStudentIDs: userId }] }],
      };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Query(type => [Course])
  static getCourseForUser(
    @Arg('userId', type => ID) userId: string,
    @Arg('filter', type => CourseFilter) filter: CourseFilter,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = filter.getMongoFilter();
      const collection = await getRawCollection<Course>(this._collectionName);
      const res = await collection
        .find({
          $and: [selector, { $or: [{ osStudentIDs: userId }, { usStudentIDs: userId }] }],
        })
        .toArray();

      if (!res) {
        return [];
      }

      return res as Course[];
    };
  }
}

if (A.isServer) {
  const col = getRawCollection<any>(Course._collectionName);

  // Create DB Indices
  void col.then(async db => {
    await db.createIndex({ osStudentIDs: 1 });
    await db.createIndex({ usStudentIDs: 1 });
    await db.createIndex({ leKoIDs: 1 });
    await db.createIndex({ schuKoIDs: 1 });
    await db.createIndex({ kpfID: 1 });
  });
}
